import { useState, useEffect } from "react";
import { 
  Trash2, 
  CheckCircle, 
  Loader2,
  Zap,
  RefreshCw,
  Settings2,
  Save,
  Eye,
  EyeOff
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FacebookIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0z"/>
  </svg>
);

const TikTokIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

export default function SettingsPage() {
  const [loading, setLoading] = useState(true);
  const [connectedPage, setConnectedPage] = useState(null);
  const [tiktokAccount, setTiktokAccount] = useState(null);
  const [instagramAccount, setInstagramAccount] = useState(null);
  const [refreshingFb, setRefreshingFb] = useState(false);
  const [refreshingTt, setRefreshingTt] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tiktokConfig, setTiktokConfig] = useState({ client_key: "", client_secret: "", redirect_uri: "" });
  const [savingConfig, setSavingConfig] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  useEffect(() => {
    fetchAllConnections();
  }, []);

  const fetchAllConnections = async () => {
    setLoading(true);
    try {
      const [fbRes, ttRes, credRes, meRes] = await Promise.all([
        axios.get(`${API}/facebook/connected-page`).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/tiktok/connected`).catch(() => ({ data: { connected: false } })),
        axios.get(`${API}/credentials`).catch(() => ({ data: [] })),
        axios.get(`${API}/auth/me`).catch(() => ({ data: {} }))
      ]);
      setConnectedPage(fbRes.data);
      setTiktokAccount(ttRes.data);
      
      const userIsAdmin = meRes.data?.is_admin === true;
      setIsAdmin(userIsAdmin);

      if (userIsAdmin) {
        try {
          const configRes = await axios.get(`${API}/admin/tiktok-config`);
          setTiktokConfig({
            client_key: configRes.data.client_key || "",
            client_secret: "",
            redirect_uri: configRes.data.redirect_uri || `${window.location.origin}/auth/tiktok/callback`
          });
        } catch (e) {
          // Not configured yet
        }
      }

      const igCred = credRes.data.find(c => c.platform === "instagram");
      if (igCred?.is_connected) {
        setInstagramAccount(igCred);
      }
    } catch (error) {
      console.error("Error fetching connections:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectFacebook = async () => {
    setRefreshingFb(true);
    try {
      const redirectUri = `${window.location.origin}/auth/facebook/callback`;
      const response = await axios.get(`${API}/facebook/oauth/authorize`, {
        params: { redirect_uri: redirectUri }
      });
      window.location.href = response.data.oauth_url;
    } catch (error) {
      toast.error("Erreur lors de la connexion Facebook");
      setRefreshingFb(false);
    }
  };

  const handleConnectTikTok = async () => {
    setRefreshingTt(true);
    try {
      const redirectUri = `${window.location.origin}/auth/tiktok/callback`;
      const response = await axios.get(`${API}/tiktok/oauth/authorize`, {
        params: { redirect_uri: redirectUri }
      });
      window.location.href = response.data.oauth_url;
    } catch (error) {
      toast.error("Erreur lors de la connexion TikTok");
      setRefreshingTt(false);
    }
  };

  const handleSaveTikTokConfig = async () => {
    if (!tiktokConfig.client_key) {
      toast.error("Veuillez entrer le Client Key");
      return;
    }
    setSavingConfig(true);
    try {
      const payload = {
        client_key: tiktokConfig.client_key,
        client_secret: tiktokConfig.client_secret,
        redirect_uri: tiktokConfig.redirect_uri || `${window.location.origin}/auth/tiktok/callback`
      };
      const response = await axios.post(`${API}/admin/tiktok-config`, payload);
      toast.success("Configuration TikTok sauvegardée!");
      setTiktokConfig(prev => ({ ...prev, redirect_uri: response.data.redirect_uri }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleDisconnect = async (platform) => {
    try {
      await axios.delete(`${API}/credentials/${platform}`);
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} déconnecté`);
      await fetchAllConnections();
    } catch (error) {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Paramètres</h1>
        <p className="text-muted-foreground mt-1">Connectez vos réseaux sociaux</p>
      </div>

      {/* Facebook */}
      <Card className={connectedPage?.connected ? "border-emerald-200" : "border-blue-200"}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white bg-[#1877F2]">
                <FacebookIcon />
              </div>
              <div>
                <CardTitle className="text-xl">Facebook</CardTitle>
                <CardDescription>
                  {connectedPage?.connected ? `Page: ${connectedPage.page_name}` : "Connectez votre Page Facebook"}
                </CardDescription>
              </div>
            </div>
            {connectedPage?.connected ? (
              <Badge className="bg-emerald-100 text-emerald-700 gap-1" data-testid="fb-connected-badge">
                <CheckCircle className="w-3 h-3" /> Connecté
              </Badge>
            ) : (
              <Badge variant="secondary">Non connecté</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {connectedPage?.connected ? (
            <>
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="flex items-center gap-4">
                  {connectedPage.picture_url && (
                    <img src={connectedPage.picture_url} alt="" className="w-12 h-12 rounded-xl" />
                  )}
                  <div>
                    <p className="font-semibold text-emerald-800">{connectedPage.page_name}</p>
                    <p className="text-sm text-emerald-700">
                      {connectedPage.fan_count ? `${connectedPage.fan_count} fans` : ""}
                    </p>
                  </div>
                </div>
              </div>

              {instagramAccount && (
                <div className="p-4 rounded-xl bg-pink-50 border border-pink-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#f09433] via-[#dc2743] to-[#bc1888] flex items-center justify-center text-white">
                      <InstagramIcon />
                    </div>
                    <div>
                      <p className="font-semibold text-pink-800">Instagram lié</p>
                      <p className="text-sm text-pink-700">{instagramAccount.username || instagramAccount.business_account_id}</p>
                    </div>
                    <Badge className="bg-pink-100 text-pink-700 gap-1 ml-auto">
                      <CheckCircle className="w-3 h-3" /> Lié
                    </Badge>
                  </div>
                </div>
              )}

              <Separator />
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleConnectFacebook} disabled={refreshingFb} className="gap-2" data-testid="reconnect-facebook">
                  {refreshingFb ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Reconnecter
                </Button>
                <Button variant="outline" onClick={() => handleDisconnect("facebook")} className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="disconnect-facebook">
                  <Trash2 className="w-4 h-4" /> Déconnecter
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-800 mb-2">Connectez votre Page Facebook pour publier du contenu.</p>
                <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Cliquez "Connecter via Facebook"</li>
                  <li>Autorisez l'accès à vos pages</li>
                  <li>Sélectionnez la page souhaitée</li>
                </ol>
              </div>
              <Button onClick={handleConnectFacebook} disabled={refreshingFb} className="gap-2" style={{ backgroundColor: "#1877F2" }} data-testid="connect-facebook-btn">
                {refreshingFb ? <Loader2 className="w-4 h-4 animate-spin" /> : <FacebookIcon />}
                Connecter via Facebook
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* TikTok */}
      <Card className={tiktokAccount?.connected ? "border-emerald-200" : ""}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white bg-black">
                <TikTokIcon />
              </div>
              <div>
                <CardTitle className="text-xl">TikTok</CardTitle>
                <CardDescription>
                  {tiktokAccount?.connected ? `Compte: ${tiktokAccount.display_name}` : "Connectez votre compte TikTok"}
                </CardDescription>
              </div>
            </div>
            {tiktokAccount?.connected ? (
              <Badge className="bg-emerald-100 text-emerald-700 gap-1" data-testid="tt-connected-badge">
                <CheckCircle className="w-3 h-3" /> Connecté
              </Badge>
            ) : (
              <Badge variant="secondary">Non connecté</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {tiktokAccount?.connected ? (
            <>
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="flex items-center gap-4">
                  {tiktokAccount.avatar_url && (
                    <img src={tiktokAccount.avatar_url} alt="" className="w-12 h-12 rounded-xl" />
                  )}
                  <div>
                    <p className="font-semibold text-emerald-800">{tiktokAccount.display_name}</p>
                    <p className="text-sm text-emerald-700">TikTok connecté</p>
                  </div>
                </div>
              </div>
              <Separator />
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleConnectTikTok} disabled={refreshingTt} className="gap-2" data-testid="reconnect-tiktok">
                  {refreshingTt ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Reconnecter
                </Button>
                <Button variant="outline" onClick={() => handleDisconnect("tiktok")} className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="disconnect-tiktok">
                  <Trash2 className="w-4 h-4" /> Déconnecter
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-800 mb-2">Connectez votre compte TikTok pour publier des vidéos.</p>
                <ol className="text-sm text-slate-700 space-y-1 list-decimal list-inside">
                  <li>Cliquez "Connecter via TikTok"</li>
                  <li>Autorisez l'application</li>
                  <li>Publiez des vidéos depuis SocialHub</li>
                </ol>
              </div>
              <Button onClick={handleConnectTikTok} disabled={refreshingTt} className="gap-2 bg-black hover:bg-gray-800" data-testid="connect-tiktok-btn">
                {refreshingTt ? <Loader2 className="w-4 h-4 animate-spin" /> : <TikTokIcon />}
                Connecter via TikTok
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Admin TikTok API Configuration */}
      {isAdmin && (
        <Card className="border-amber-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-100 text-amber-700">
                  <Settings2 className="w-6 h-6" />
                </div>
                <div>
                  <CardTitle className="text-xl">Configuration TikTok API</CardTitle>
                  <CardDescription>Administrateur uniquement</CardDescription>
                </div>
              </div>
              <Badge className="bg-amber-100 text-amber-700">Admin</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-sm text-amber-800">
                Configurez les identifiants de votre app TikTok Developer ici. Ces clés sont utilisées pour la connexion OAuth TikTok.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tt-client-key">Client Key</Label>
                <Input
                  id="tt-client-key"
                  type="text"
                  placeholder="Votre TikTok Client Key"
                  value={tiktokConfig.client_key}
                  onChange={(e) => setTiktokConfig(prev => ({ ...prev, client_key: e.target.value }))}
                  data-testid="tiktok-client-key-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tt-client-secret">Client Secret</Label>
                <div className="relative">
                  <Input
                    id="tt-client-secret"
                    type={showSecret ? "text" : "password"}
                    placeholder="Votre TikTok Client Secret (laisser vide pour garder l'ancien)"
                    value={tiktokConfig.client_secret}
                    onChange={(e) => setTiktokConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                    data-testid="tiktok-client-secret-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tt-redirect-uri">Redirect URI</Label>
                <Input
                  id="tt-redirect-uri"
                  type="text"
                  placeholder="https://votre-domaine.com/auth/tiktok/callback"
                  value={tiktokConfig.redirect_uri}
                  onChange={(e) => setTiktokConfig(prev => ({ ...prev, redirect_uri: e.target.value }))}
                  data-testid="tiktok-redirect-uri-input"
                />
                <p className="text-xs text-muted-foreground">Cette URI doit correspondre exactement à celle configurée dans TikTok Developer</p>
              </div>
            </div>

            <Separator />

            <Button onClick={handleSaveTikTokConfig} disabled={savingConfig} className="gap-2" data-testid="save-tiktok-config">
              {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Sauvegarder la configuration
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Help Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5" /> Comment ça marche
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
              <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded bg-[#1877F2] flex items-center justify-center text-white text-xs">f</div>
                Facebook
              </h3>
              <p className="text-sm text-blue-700">Connectez-vous via Facebook OAuth pour publier sur votre Page.</p>
            </div>
            <div className="p-4 rounded-xl bg-pink-50 border border-pink-200">
              <h3 className="font-semibold text-pink-800 flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded bg-gradient-to-br from-[#f09433] to-[#bc1888] flex items-center justify-center text-white text-xs">ig</div>
                Instagram
              </h3>
              <p className="text-sm text-pink-700">Se connecte automatiquement si votre Instagram est lié à votre Page Facebook.</p>
            </div>
            <div className="p-4 rounded-xl bg-slate-100 border border-slate-200">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded bg-black flex items-center justify-center text-white text-xs">T</div>
                TikTok
              </h3>
              <p className="text-sm text-slate-700">Connectez-vous via TikTok OAuth pour publier des vidéos.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
